import { useState, useEffect } from "react";
import { useAuth } from "@/app/AuthContext";
import { useSchoolProfile } from "@/app/SchoolProfileContext";

import { useEvaluationForm } from "./hooks/useEvaluationForm";
import { useUserData } from "./hooks/useUserData";
import { useClassesData } from "./hooks/useClassesData";

import type { Step } from "./types";
import { EVALUATION_TYPES, INITIAL_FORM_DATA } from "./constants";
import { validateFormData } from "./utils";

import { generateProvaWithAI } from "./api";

import { downloadProvaAsPDF } from "./pdf/PDFRenderer";

import { ModalLayout } from "./layout/ModalLayout";
import { ModalHeader } from "./layout/ModalHeader";

import { FormStep } from "./steps/FormStep";
import { PreviewStep } from "./steps/PreviewStep";

import { createMaterial } from "@/lib/api";

interface EvaluationCreatorProps {
  onClose: () => void;
  initialData?: {
    title?: string;
    subject?: string;
    class?: string;
    type?: string;
    content?: string;
    topic?: string;
    difficulty?: "easy" | "medium" | "hard";
  };
}

export default function EvaluationCreator({
  onClose,
  initialData,
}: EvaluationCreatorProps) {
  const { token, user } = useAuth();
  const { activeProfile } = useSchoolProfile();

  // Prepara dados iniciais para o formulário
  const initialFormData = initialData
    ? {
        title: initialData.title || "",
        subject: initialData.subject || "",
        class: initialData.class || "",
        type: (initialData.type as any) || "practical_activity",
        customPrompt: initialData.content || "",
        difficulty: initialData.difficulty || "medium",
      }
    : undefined;

  const { formData, setFormData, updateField, updateAllFields } =
    useEvaluationForm(initialFormData);
  const { userData, loading: loadingUser } = useUserData(token);
  const { classes, loading: loadingClasses } = useClassesData(
    token,
    activeProfile?.id || null,
  );

  const [step, setStep] = useState<Step>("form");
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedContent, setGeneratedContent] = useState<any>(null);
  const [error, setError] = useState("");

  const isDarkMode =
    window.matchMedia &&
    window.matchMedia("(prefers-color-scheme: dark)").matches;
  const isDark =
    document.documentElement.classList.contains("dark") || isDarkMode;

  useEffect(() => {
    const professorName = user?.name || userData?.full_name || "";
    const schoolName = activeProfile?.name || activeProfile?.institution || "";

    if (professorName || schoolName) {
      setFormData((prev) => ({
        ...prev,
        professorName,
        schoolName,
      }));
    }
  }, [user, userData, activeProfile, setFormData]);

  useEffect(() => {
    if (formData.class && classes.length > 0) {
      const selectedClass = classes.find((c) => c.id === formData.class);
      if (selectedClass?.subject) {
        updateField("subject", selectedClass.subject);
      }
    }
  }, [formData.class, classes, updateField]);

  const handleGenerateWithAI = async () => {
    if (!validateFormData(formData)) {
      setError("Preencha disciplina, turma e título antes de gerar com IA");
      return;
    }

    setIsGenerating(true);
    setError("");

    try {
      const content = await generateProvaWithAI(formData);
      setGeneratedContent(content);
      setStep("preview");

      // Persist the generated evaluation automatically so it appears in FilesManager
      // This is not a user action, but a required side-effect of the AI generation flow
      if (token && activeProfile) {
        const payload = {
          title: formData.title,
          content: JSON.stringify({
            kind: "ai_evaluation",
            data: content,
            formData: formData,
            subject: formData.subject,
          }),
          type: "document",
          school_profile_id: activeProfile.id,
        };
        createMaterial(payload, token)
          .then(() => {
            console.log(
              "EvaluationCreator: Material criado com sucesso, disparando evento...",
            );
            window.dispatchEvent(
              new CustomEvent("materialCreated", {
                detail: { type: "evaluation" },
              }),
            );
          })
          .catch(console.error);
      }
    } catch (err: any) {
      setError(err.message || "Erro ao conectar com o servidor");
      console.error("Erro na geração:", err);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDownloadPDF = () => {
    downloadProvaAsPDF(formData, generatedContent, userData);
  };

  const renderContent = () => {
    if (step === "form") {
      return (
        <FormStep
          isDark={isDark}
          formData={formData}
          onFormChange={updateField}
          userData={userData}
          loadingUser={loadingUser}
          classes={classes}
          loadingClasses={loadingClasses}
          activeProfile={activeProfile}
          evaluationTypes={EVALUATION_TYPES}
          isGenerating={isGenerating}
          error={error}
          onGenerate={handleGenerateWithAI}
        />
      );
    }

    if (step === "preview" && generatedContent) {
      return (
        <PreviewStep
          formData={formData}
          generatedContent={generatedContent}
          userData={userData}
          evaluationTypes={EVALUATION_TYPES}
        />
      );
    }

    return null;
  };

  return (
    <ModalLayout
      isDark={isDark}
      header={
        <ModalHeader
          isDark={isDark}
          step={step}
          onStepChange={setStep}
          onDownloadPDF={handleDownloadPDF}
          onClose={onClose}
        />
      }
      content={renderContent()}
      onBackdropClick={onClose}
    />
  );
}
